/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import 'chartjs-plugin-streaming';
import { ActivatedRoute, Router } from '@angular/router';
import { Component, Input, OnInit, } from '@angular/core';
import { MetricsEnum } from '../../enums';
import { RbacService, UtilsService } from '../../../../../core/services';
import { WidgetBaseComponent } from '../widgetBase.component';
import { WidgetInterface } from '../../interfaces/widget.interface';
import { WidgetService } from '../../services/utilities/widget.service';
import { WidgetStateEnum } from '../../../../../core/enums';
import { formatDate } from '@angular/common';
import { takeUntil } from 'rxjs/operators';

interface DiskUsage {
    used: number;
    free: number;
}

interface DiskResponse {
    root: Record<string, DiskUsage>;
    log: Record<string, DiskUsage>;
}

@Component({
    selector: 'app-widget-sys-metrics',
    templateUrl: './systemMetrics.widget.html',
    styleUrls: ['./systemMetrics.widget.scss']
})

export class SystemMetricsWidgetComponent extends WidgetBaseComponent implements WidgetInterface, OnInit {
    /**
     * Override socket response and use the value we pass manually
     */
    @Input()
    set metricValue(val: any) {
        this._metricValue = val;
    }

    /**
     * Define the number of columns each widget should 'span'.
     */
    @Input() public colSpan: number;

    /**
     * Define our metric from the MetricsEnum.
     */
    @Input() public metric: string = null;

    /**
     * Store the widget id.
     */
    @Input() public id: string;

    /**
     * If we have external data from ws feed use that otherwise emit.
     * @param externalData
     */
    @Input() set externalData(externalData: any) {
        this._externalData = externalData;
    }

    /**
     * Open metricsEnum to the view.
     */
    public MetricsEnum: typeof MetricsEnum = MetricsEnum;

    /**
     * Widget loading state.
     */
    public loading: boolean = false;

    /**
     * Set metric scale for widget.
     */
    public scale: string;

    /**
     * Widget state.
     */
    public state: WidgetStateEnum;

    /**
     * Array of disks.
     */
    public diskData: any[] = [{ label: 'disk', value: 0 }];

    /**
     * Graph datasets.
     */
    public data = {
        datasets: []
    };

    /**
     * Graph options.
     * DOCS - https://www.chartjs.org/docs/latest/
     */
    public graphOptions: any = {
        responsive: true,
        animation: { duration: 1 },
        elements: { point: { radius: 0 } },
        scales: {
            xAxes: {
                ticks: {
                    autoSkip: true,
                }
            },
            yAxes: { min: 0, beginAtZero: false }
        },
        tooltips: {
            mode: 'nearest',
            intersect: false
        },
        hover: {
            mode: 'nearest',
            intersect: false
        }
    };

    /**
     * Store permissions for buttons.
     */
    public permissionsLookup: any = {};

    /**
     * Store metric value.
     * @private
     */
    private _metricValue: number;

    /**
     * Widget value.
     * @private
     */
    private _value: number = 0;

    /**
     * The initial data is an array and needs to be handled differently.
     * @private
     */
    private initData: boolean = true;

    /**
     * Store a range of colours for the graphs to use.
     * @private
     */
    private colors: string[] = [
        '#00b391',
        '#f41171',
        '#5c61d4',
        '#ffa340',
        '#333',
        '#c200ff',
        '#faee00',
        '#54009c',
    ];

    /**
     * Store external data
     * @private
     */
    private _externalData: any;

    constructor(
        private activatedRoute: ActivatedRoute,
        private rbacService: RbacService,
        private router: Router,
        private widgetService: WidgetService,
        utils: UtilsService
    ) {
        super(utils);
        this.resetLocalVariables();

        this.subscribeToWidgetService();
        this.registerButtons();
    }

    /**
     * External data getter.
     */
    get externalData(): any {
        return this._externalData;
    }

    get metricValue() {
        return this._metricValue;
    }

    set value(data: any) {
        this._value = data;
    }

    get value() {
        return this._value;
    }

    public ngOnInit(): void {
        // Set up our graph data if this is a graph widget
        switch (this.metric) {
            case MetricsEnum.NETWORKGRAPH:
                this.scale = 'kbs';
                this.data.datasets = [
                    {
                        label: 'rx',
                        lineTension: 0,
                        data: [{ x: 0, y: 0 }]
                    },
                    {
                        label: 'tx',
                        lineTension: 0,
                        data: [{ x: 0, y: 0 }]
                    },
                ];
                break;
            case MetricsEnum.LOADAVERAGEGRAPH:
                this.scale = '%';
                // this.graphOptions.scales.yAxes.ticks.max = 100;
                this.data.datasets = [
                    {
                        label: 'CPU',
                        lineTension: 0,
                        data: [{ x: 0, y: 0 }],
                        borderColor: '#54009c',
                        backgroundColor: '#54009c'
                    },
                ];
                break;
            case MetricsEnum.MEMORYGRAPH:
                this.scale = 'Mb';
                this.data.datasets = [
                    {
                        label: 'Free',
                        lineTension: 0,
                        data: [{ x: 0, y: 0 }],
                        borderColor: '#00b391',
                        backgroundColor: '#00b391',
                    },
                    {
                        label: 'Used',
                        lineTension: 0,
                        data: [{ x: 0, y: 0 }],
                        borderColor: '#f41171',
                        backgroundColor: '#f41171',
                    },
                    {
                        label: 'Buffered',
                        lineTension: 0,
                        data: [{ x: 0, y: 0 }],
                        borderColor: '#5c61d4',
                        backgroundColor: '#5c61d4',
                    },
                    {
                        label: 'Cached',
                        lineTension: 0,
                        data: [{ x: 0, y: 0 }],
                        borderColor: '#ffa340',
                        backgroundColor: '#ffa340',
                    },
                ];
                break;
        }
    }

    /**
     * Link to details page.
     */
    public linkToDetailsPage(metric: string): void {
        const urlParam: string = metric.replace(/\s+/g, '-').toLowerCase();
        this.router.navigate(['details', urlParam, 'day'], { relativeTo: this.activatedRoute }).then().catch();
    }

    /**
     * Get widget details.
     */
    public getWidgetDetails(): void {
        // No details to grab as we use SOCKETS
    }

    /**
     * Reset the local variable.
     */
    public resetLocalVariables(): void {
        this.value = 0;
        this.loading = true;
    }

    /**
     * Subscribe to widget service.
     * @private
     */
    private subscribeToWidgetService(): void {
        this.widgetService.statusDataEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response): void => {
                    this.state = WidgetStateEnum.WARNING;
                    this.loading = false;
                    this.socketListenerSwitchStatement(response);
                }
            });

        this.widgetService.socketClosedEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (hasClosed): void => {
                    if (hasClosed) {
                        this.state = WidgetStateEnum.WARNING;
                    }
                }
            });

        this.widgetService.sendSubscriptionConfirmEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (service): void => {
                    if (service === 'systemStats') {
                        this.widgetService.receiveSubscriptionConfirmEmitter.next({
                            service: 'systemStats',
                            event: 'stats'
                        });
                    }
                }
            });
    }

    /**
     * Switch statement for each widget usage.
     * @private
     * @param response
     */
    private socketListenerSwitchStatement(response: any): void {
        switch (this.metric) {
            case MetricsEnum.CPU:
                this.value = this.formatSingleCpuItem(response.cpu);
                this.state = response.cpu ? WidgetStateEnum.OK : WidgetStateEnum.CRITICAL;
                break;
            case MetricsEnum.MEMORY:
                this.value = this.formatSingleMemoryItem(response.memory);
                this.state = response.memory ? WidgetStateEnum.OK : WidgetStateEnum.CRITICAL;
                break;
            case MetricsEnum.DISKS:
                this.diskData = this.formatSingleDiskItem(response.disks);
                this.state = response.disks ? WidgetStateEnum.OK : WidgetStateEnum.CRITICAL;
                break;
            case MetricsEnum.NETWORKGRAPH:
                this.setNetworkGraphData(response.interfaces);
                this.state = response.interfaces ? WidgetStateEnum.OK : WidgetStateEnum.CRITICAL;
                break;
            case MetricsEnum.LOADAVERAGEGRAPH:
                this.setCpuGraphData(response.cpu);
                this.state = response.cpu ? WidgetStateEnum.OK : WidgetStateEnum.CRITICAL;
                break;
            case MetricsEnum.MEMORYGRAPH:
                this.setMemoryGraphData(response.memory);
                this.state = response.memory ? WidgetStateEnum.OK : WidgetStateEnum.CRITICAL;
                break;
        }
    }

    /**
     * Format single CPU item.
     * @param response
     * @private
     */
    private formatSingleCpuItem(response: any): any {
        const value: any = Object.values(response)[0];

        return Math.round(typeof value === 'number' ? value : 0);
    }

    /**
     * Format single memory item.
     * @param response
     * @private
     */
    private formatSingleMemoryItem(response: any): any {
        const memory: any = Object.values(response)[0];
        const totalMemory: any = memory['used'] + memory['free'];

        return Math.round((memory['used'] / totalMemory) * 100);
    }

    /**
     * Set our data array for our disk widget.
     * @private
     * @param response
     */
    private formatSingleDiskItem(response: DiskResponse): { label: string; value: number }[] {
        const rootValues: any = Object.values(response.root)[0];
        const rootPercentage: number = (rootValues.used / (rootValues.used + rootValues.free)) * 100;
        const logValues: any = Object.values(response.log)[0];
        const logPercentage: number = (logValues.used / (logValues.used + logValues.free)) * 100;

        return [
            { label: 'root', value: Math.round(rootPercentage) },
            { label: 'log', value: Math.round(logPercentage) }
        ];
    }

    /**
     * Set the network graph data.
     * @param interfaces
     * @private
     */
    private setNetworkGraphData(interfaces: any): void {
        const initTenMinutes: any[] = [];
        let isInitData: boolean = false;

        // Loop through all interfaces to get each set
        for (const interfaceName in interfaces) {
            if (interfaces.hasOwnProperty(interfaceName)) {
                const iFace: any = interfaces[interfaceName];

                // Loop through each interface e.g. ens33
                for (const rxTxProperties in iFace) {
                    if (iFace.hasOwnProperty(rxTxProperties)) {
                        isInitData = Object.values(iFace[rxTxProperties]).length > 1;
                        const dataGroup: any = iFace[rxTxProperties];
                        const lineData: any[] = [];

                        // loop through both the TX and RX values
                        for (const item in dataGroup) {
                            if (dataGroup.hasOwnProperty(item)) {
                                const key: string = item;
                                lineData.push({
                                    x: this.formatDate(key),
                                    y: dataGroup[item]
                                });
                            }
                        }

                        if (!isInitData) {
                            const index: number = this.data.datasets.findIndex(
                                (dataSet: any) => dataSet.label === `${interfaceName} - ${rxTxProperties}`
                            );

                            if (index !== -1) {
                                const data: any = this.data.datasets[index];
                                data.data.push(lineData[0]);
                                data.data.shift();
                                data.borderColor = this.colors[index];
                                data.backgroundColor = this.colors[index];
                            }
                        }

                        if (isInitData) {
                            initTenMinutes.push({
                                label: `${interfaceName} - ${rxTxProperties}`,
                                fill: false,
                                lineTension: 0,
                                data: lineData,
                            });
                        }
                    }
                }
            }
        }

        if (isInitData) {
            this.data.datasets = initTenMinutes;
        }

        this.data.datasets = [...this.data.datasets];
    }

    /**
     * Set CPU graph data.
     * @param cpuValues
     * @private
     */
    private setCpuGraphData(cpuValues: any): void {
        const lineData: any[] = [];
        for (const [key, value] of Object.entries(cpuValues)) {
            lineData.push({ x: this.formatDate(key), y: value as number });

            if (!this.initData) {
                this.data.datasets[0].data.push(lineData[0]);
                this.data.datasets[0].data.shift();
            }
        }

        if (this.initData) {
            this.data.datasets[0].data = lineData;
            this.initData = false;
        }

        this.data.datasets = [...this.data.datasets];
    }

    /**
     * Set memory graph data.
     * @param memory
     * @private
     */
    private setMemoryGraphData(memory: any): void {
        const memoryTypes: string[] = ['used', 'free', 'buffered', 'cached'];
        const graphData = {
            used: [],
            free: [],
            buffered: [],
            cached: []
        };

        for (const [key, value] of Object.entries(memory)) {
            memoryTypes.forEach((type: string) => {
                graphData[type].push({
                    x: this.formatDate(key),
                    y: Math.round(value[type] / Math.pow(1024, 2))
                });
            });

            if (!this.initData) {
                memoryTypes.forEach((type: string, index: number) => {
                    this.data.datasets[index].data.push(graphData[type][0]);
                    this.data.datasets[index].data.shift();
                });
            }
        }

        if (this.initData) {
            memoryTypes.forEach((type: string, index: number) => {
                this.data.datasets[index].data = graphData[type];
            });
        }

        this.initData = false;
        this.data.datasets = [...this.data.datasets];
    }

    /**
     * Format date for X label.
     * @param timestamp
     * @private
     */
    private formatDate(timestamp: string): string {
        const date = new Date(parseInt(timestamp, 10) * 1000);

        return formatDate(date, 'HH:mm:ss', 'en-GB');
    }

    /**
     * Register button permissions
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'overview-details': [{ path: '/system-stats', methods: ['GET'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }

    protected readonly WidgetStateEnum = WidgetStateEnum;
}
